/**
 * VerifyEmailPending — iter-169l screen shown right after sign-up (or
 * when an unverified user tries to log in). UX rules:
 *
 *   • Big confirmation that the email was sent + which inbox to check.
 *   • Resend button with a 30s cooldown (and silent rate-limit on
 *     backend at 3/15min).
 *   • Inline "Wrong email? Change it" panel — lets the user re-bind
 *     the unverified account to a corrected address. Once submitted,
 *     a new verification email is dispatched.
 *   • Links back to /login and to /support if email never arrives.
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card } from "../../components/ui/card";
import { useLang } from "../../contexts/LangContext";
import { authService } from "../../services/authService";
import { formatApiError } from "../../lib/api";
import { LumfiWordmark } from "../../components/LumfiLogo";
import { CheckCircle2, Mail, Pencil, Loader2 } from "lucide-react";
import { toast } from "sonner";

const RESEND_COOLDOWN = 30;

export default function VerifyEmailPending() {
  const { lang, setLang } = useLang();
  const isEs = lang === "es";
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get("email") || "");
  const [cooldown, setCooldown] = useState(0);
  const [editing, setEditing] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [editError, setEditError] = useState("");

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleResend = async () => {
    if (cooldown > 0 || !email) return;
    setBusy(true);
    try {
      await authService.resendVerification(email);
      toast.success(isEs ? "Email reenviado" : "Email resent");
      setCooldown(RESEND_COOLDOWN);
    } catch {
      // Backend is opaque; show success regardless to avoid enumeration.
      setCooldown(RESEND_COOLDOWN);
    } finally {
      setBusy(false);
    }
  };

  const handleChangeEmail = async () => {
    if (!newEmail.trim() || newEmail.trim() === email) return;
    setBusy(true);
    setEditError("");
    try {
      await authService.updatePendingEmail(email, newEmail.trim().toLowerCase());
      const next = newEmail.trim().toLowerCase();
      setEmail(next);
      setEditing(false);
      setNewEmail("");
      setCooldown(RESEND_COOLDOWN);
      toast.success(isEs ? "Email actualizado. Te enviamos un link nuevo." : "Email updated. New link sent.");
    } catch (e) {
      const detail = e.response?.data?.detail;
      const map = {
        already_verified: isEs ? "Esa cuenta ya está verificada." : "That account is already verified.",
        email_already_in_use: isEs ? "Ese email ya está en uso." : "That email is already in use.",
        same_email: isEs ? "Es el mismo email." : "It's the same email.",
        invalid_emails: isEs ? "Email inválido." : "Invalid email.",
        rate_limited: isEs ? "Demasiados intentos. Prueba en 15 min." : "Too many attempts. Try in 15 min.",
      };
      setEditError(map[detail] || formatApiError(detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-10 safe-bottom">
      <div className="w-full max-w-md lumfi-rise-in">
        <div className="text-center mb-7 relative">
          <Link to="/"><LumfiWordmark /></Link>
          <div className="absolute top-0 right-0 inline-flex bg-white border border-slate-200 rounded-full p-0.5 shadow-sm">
            {["es", "en", "pt"].map((code) => (
              <button /* design-system: ALLOW legacy-todo */
                key={code} type="button" onClick={() => setLang(code)}
                data-testid={`verify-pending-lang-${code}`}
                className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase transition-colors ${
                  lang === code ? "bg-emerald-600 text-white" : "text-slate-500 hover:text-slate-900"
                }`}
              >{code}</button>
            ))}
          </div>
        </div>
        <Card className="rounded-2xl border-slate-200 p-7 shadow-sm" data-testid="verify-pending-card">
          <div className="flex items-center gap-2 text-emerald-700 mb-3">
            <CheckCircle2 className="h-5 w-5" />
            <h1 className="text-lg font-semibold">
              {isEs ? "Confirma tu email" : "Confirm your email"}
            </h1>
          </div>
          <p className="text-sm text-slate-600 mb-2 leading-relaxed">
            {isEs ? (
              <>Te mandamos un link de confirmación a <strong className="text-slate-900">{email || "tu email"}</strong>. Haz clic ahí para activar tu cuenta y entrar al panel. El link vence en 15 minutos.</>
            ) : (
              <>We sent a confirmation link to <strong className="text-slate-900">{email || "your email"}</strong>. Click it to activate your account and access the dashboard.</>
            )}
          </p>
          <p className="text-xs text-slate-500 mb-5">
            {isEs
              ? "¿No lo ves? Revisa la carpeta de spam — los emails vienen de soporte@lumfi.wintimeapp.com."
              : "Don't see it? Check your spam folder — emails come from soporte@lumfi.wintimeapp.com."}
          </p>

          {!editing ? (
            <>
              <div className="grid grid-cols-2 gap-2 mb-4">
                <Button
                  type="button"
                  variant="outline"
                  className="h-10"
                  disabled={cooldown > 0 || busy || !email}
                  onClick={handleResend}
                  data-testid="verify-pending-resend"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  {cooldown > 0
                    ? (isEs ? `Reenviar en ${cooldown}s` : `Resend in ${cooldown}s`)
                    : (isEs ? "Reenviar email" : "Resend email")}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="h-10"
                  onClick={() => { setEditing(true); setNewEmail(""); setEditError(""); }}
                  data-testid="verify-pending-edit-email"
                  disabled={!email}
                >
                  <Pencil className="h-4 w-4 mr-2" />
                  {isEs ? "Cambiar email" : "Change email"}
                </Button>
              </div>
              <Link
                to="/login"
                data-testid="verify-pending-back-to-login"
                className="block text-center text-xs text-slate-500 hover:text-slate-700"
              >
                {isEs ? "Volver al login" : "Back to login"}
              </Link>
            </>
          ) : (
            <div className="space-y-3" data-testid="verify-pending-edit-panel">
              <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">
                {isEs ? "Nuevo email" : "New email"}
              </Label>
              <Input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                autoFocus
                placeholder="tú@ejemplo.com"
                data-testid="verify-pending-new-email-input"
                className="h-12"
              />
              {editError && <p className="text-xs text-red-600" data-testid="verify-pending-edit-error">{editError}</p>}
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="h-10"
                  onClick={() => { setEditing(false); setEditError(""); }}
                  data-testid="verify-pending-cancel-edit"
                >
                  {isEs ? "Cancelar" : "Cancel"}
                </Button>
                <Button
                  type="button"
                  className="h-10 bg-slate-900 hover:bg-slate-800 text-white"
                  disabled={busy || !newEmail.trim() || newEmail.trim() === email}
                  onClick={handleChangeEmail}
                  data-testid="verify-pending-save-email"
                >
                  {busy
                    ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {isEs ? "Guardando…" : "Saving…"}</>
                    : (isEs ? "Actualizar y reenviar" : "Update & resend")}
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
